import { useEffect, useCallback, useRef } from 'react';

/**
 * Web Push subscription hook.
 *
 * Silently subscribes the current employee's device to push notifications
 * once they've logged in and the browser has granted permission. Registers
 * the resulting subscription with the backend so it can be pushed to later.
 *
 * Uses the standards-based Web Push API + VAPID (no third-party provider).
 * Works on Chrome, Firefox, Edge, Safari 16.4+ (installed PWA on iOS).
 */
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
};

export function usePushSubscription(api, isEligible) {
  const subscribedRef = useRef(false);

  const subscribe = useCallback(async () => {
    if (subscribedRef.current) return { subscribed: true };
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return { subscribed: false, reason: 'unsupported' };
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return { subscribed: false, reason: 'permission-denied' };

      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();

      if (!sub) {
        const keyRes = await api.get('/push/public-key');
        const pk = keyRes.data?.public_key;
        if (!pk) return { subscribed: false, reason: 'no-key' };
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(pk),
        });
      }

      const json = sub.toJSON();
      await api.post('/push/subscribe', {
        endpoint: json.endpoint,
        keys: json.keys,
        ua: navigator.userAgent,
      });
      subscribedRef.current = true;
      return { subscribed: true };
    } catch (err) {
      return { subscribed: false, reason: err?.message || 'error' };
    }
  }, [api]);

  useEffect(() => {
    if (!isEligible) return;
    // Only auto-subscribe if permission was previously granted (no popup on first visit)
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      subscribe();
    }
  }, [isEligible, subscribe]);

  return { subscribe };
}

export default usePushSubscription;
